/**
 * Boona Help — the support widget, shared by the journal and Boona Direct
 * (boona repo: static/support/widget.js, docs/codebase/support/README.md §6.1).
 *
 * The widget is one vanilla script served by boona.app, rendered in its own
 * shadow root (Tailwind can't reach in), and it fetches every word it shows
 * from the support API in the language we pass. We only load it once, hand it
 * the journal's bearer token on each call, and open it from our own buttons.
 */

const SUPPORT_ORIGIN = "https://boona.app";
// the same key AuthRegistry keeps the journal's session under
const TOKEN_STORAGE_KEY = "boona:access_token";

type BoonaSupport = {
  mount: (options: Record<string, unknown>) => unknown;
  open: () => void;
  close: () => void;
};

declare global {
  interface Window {
    BoonaSupport?: BoonaSupport;
  }
}

let loading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.BoonaSupport) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${SUPPORT_ORIGIN}/static/support/widget.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loading = null; // a later mount may try again
      reject(new Error("help widget failed to load"));
    };
    document.head.appendChild(script);
  });
  return loading;
}

function token(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Mount once per app; a failure to load never breaks the page. */
export function mountHelp(options: { source: "journal" | "inbox"; lang: string; launcher?: boolean }): Promise<void> {
  return loadScript()
    .then(() => {
      window.BoonaSupport?.mount({
        skin: "app",
        source: options.source,
        lang: options.lang,
        api: `${SUPPORT_ORIGIN}/api/support`,
        launcher: options.launcher !== false,
        auth: token,
      });
    })
    .catch(() => undefined);
}

export function openHelp(): void {
  window.BoonaSupport?.open();
}
